// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Header, Form, Button, Icon, Message } from 'semantic-ui-react';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import { brandingApi } from '../api/brandingApi';
import EmailTemplateSection from '../components/EmailTemplateSection';
import { AddressForm, type AddressData } from '../components/AddressForm';
import { DEFAULT_COUNTRY, SUPPORTED_COUNTRIES } from '../config/addressConfig';

function BrokerageFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const defaultCountry = SUPPORTED_COUNTRIES.find(c => c.value === DEFAULT_COUNTRY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Brokerage, 'id'>>({
    name: '',
    subdomain: '',
    registrationNumber: '',
    contactEmail: '',
    contactPhone: '',
    // Address fields (ISO 19160 compliant)
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    townCity: '',
    stateProvinceCounty: '',
    postalCode: '',
    countryCode: DEFAULT_COUNTRY,
    country: defaultCountry?.text || 'Ireland',
    status: 'ACTIVE',
    // Email configuration
    emailAddress: '',
    emailDisplayName: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpUseTls: true,
    smtpUseSsl: false,
    // Email templates
    advisorInvitationTemplate: '',
    clientInvitationTemplate: '',
    advisorInvitationSubject: '',
    clientInvitationSubject: '',
    // CRM Integration
    crmSolutionName: ''
  });

  useEffect(() => {
    // Load brokerage data if in edit mode
    if (isEditMode && id) {
      loadBrokerage(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadBrokerage = async (brokerageId: string) => {
    setLoading(true);
    try {
      const brokerage = await brokerageApi.getById(brokerageId);
      setFormData({
        name: brokerage.name,
        subdomain: brokerage.subdomain,
        registrationNumber: brokerage.registrationNumber || '',
        contactEmail: brokerage.contactEmail || '',
        contactPhone: brokerage.contactPhone || '',
        addressLine1: brokerage.addressLine1 || '',
        addressLine2: brokerage.addressLine2 || '',
        addressLine3: brokerage.addressLine3 || '',
        townCity: brokerage.townCity || '',
        stateProvinceCounty: brokerage.stateProvinceCounty || '',
        postalCode: brokerage.postalCode || '',
        countryCode: brokerage.countryCode || DEFAULT_COUNTRY,
        country: brokerage.country || defaultCountry?.text || 'Ireland',
        status: brokerage.status || 'ACTIVE',
        emailAddress: brokerage.emailAddress || '',
        emailDisplayName: brokerage.emailDisplayName || '',
        smtpHost: brokerage.smtpHost || '',
        smtpPort: brokerage.smtpPort || 587,
        smtpUsername: brokerage.smtpUsername || '',
        smtpPassword: '', // Don't populate password for security
        smtpUseTls: brokerage.smtpUseTls ?? true,
        smtpUseSsl: brokerage.smtpUseSsl ?? false,
        advisorInvitationTemplate: brokerage.advisorInvitationTemplate || '',
        clientInvitationTemplate: brokerage.clientInvitationTemplate || '',
        advisorInvitationSubject: brokerage.advisorInvitationSubject || '',
        clientInvitationSubject: brokerage.clientInvitationSubject || '',
        crmSolutionName: brokerage.crmSolutionName || ''
      });
    } catch (err) {
      console.error('Failed to load brokerage:', err);
      setError('Failed to load brokerage');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (field: keyof AddressData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailTemplateChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEditMode && id) {
        await brokerageApi.update(id, formData);
      } else {
        const created = await brokerageApi.create(formData);
        // Initialize branding in the Branding Service with defaults
        if (created.id) {
          try {
            await brandingApi.initBranding({
              tenantId: created.id,
              subdomain: formData.subdomain,
              displayName: formData.name,
            });
          } catch (brandingErr) {
            console.warn('Failed to initialize branding:', brandingErr);
          }
        }
      }
      navigate('/brokerages');
    } catch (err) {
      const fallbackMessage = isEditMode ? 'Failed to update brokerage' : 'Failed to create brokerage';
      setError(err instanceof Error ? err.message : fallbackMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container style={{ padding: '2rem' }}>
      <Header as='h1'>
        <Icon name={isEditMode ? 'edit' : 'plus'} />
        <Header.Content>
          {isEditMode ? 'Edit Brokerage' : 'Create New Brokerage'}
          <Header.Subheader>
            {isEditMode ? 'Update brokerage information' : 'Add a new brokerage to the platform'}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {isEditMode && (
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <Button primary onClick={() => navigate(`/brokerages/${id}/providers`)}>
            <Icon name='building' />
            Configure Providers & Policy Types
          </Button>
          <Button color='purple' onClick={() => navigate(`/branding/${id}/edit`)}>
            <Icon name='paint brush' />
            Configure Branding
          </Button>
          <Button color='teal' onClick={() => navigate(`/brokerages/${id}/integrations`)}>
            <Icon name='key' />
            External Integrations
          </Button>
        </div>
      )}

      <Form onSubmit={handleSubmit} loading={loading}>
          <Form.Group widths='equal'>
            <Form.Field required>
              <label htmlFor='brokerage-name'>Brokerage Name</label>
              <input
                id='brokerage-name'
                name='name'
                value={formData.name}
                onChange={handleChange}
                placeholder='Enter brokerage name'
                required
              />
            </Form.Field>
            <Form.Field required>
              <label htmlFor='brokerage-subdomain'>Subdomain</label>
              <input
                id='brokerage-subdomain'
                name='subdomain'
                value={formData.subdomain}
                onChange={handleChange}
                placeholder='acme-insurance'
                pattern='^[a-z0-9][a-z0-9\-]{0,61}[a-z0-9]$'
                title='Subdomain must contain only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen'
                disabled={isEditMode}
                required
              />
              {isEditMode && (
                <small style={{ color: '#999' }}>Subdomain cannot be changed after creation</small>
              )}
            </Form.Field>
          </Form.Group>

          <Form.Group widths='equal'>
            <Form.Field required>
              <label htmlFor='brokerage-registration-number'>Registration Number</label>
              <input
                id='brokerage-registration-number'
                name='registrationNumber'
                value={formData.registrationNumber}
                onChange={handleChange}
                placeholder='Enter registration number'
                required
              />
            </Form.Field>
            <Form.Field />
          </Form.Group>

          <Form.Group widths='equal'>
            <Form.Field required>
              <label htmlFor='brokerage-contact-email'>Contact Email</label>
              <input
                id='brokerage-contact-email'
                type='email'
                name='contactEmail'
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder='email@example.com'
                required
              />
            </Form.Field>
            <Form.Field required>
              <label htmlFor='brokerage-contact-phone'>Contact Phone</label>
              <input
                id='brokerage-contact-phone'
                type='tel'
                name='contactPhone'
                value={formData.contactPhone}
                onChange={handleChange}
                placeholder='(555) 555-5555'
                required
              />
            </Form.Field>
          </Form.Group>

          <Header as='h3' dividing style={{ marginTop: '30px' }}>
            Address Information
            <Header.Subheader>Physical address of the brokerage</Header.Subheader>
          </Header>

          <AddressForm
            address={{
              addressLine1: formData.addressLine1 || '',
              addressLine2: formData.addressLine2,
              addressLine3: formData.addressLine3,
              townCity: formData.townCity || '',
              stateProvinceCounty: formData.stateProvinceCounty,
              postalCode: formData.postalCode || '',
              countryCode: formData.countryCode || DEFAULT_COUNTRY,
              country: formData.country || defaultCountry?.text || 'Ireland',
            }}
            onChange={handleAddressChange}
          />

          <Form.Field required>
            <label htmlFor='brokerage-status'>Status</label>
            <select
              id='brokerage-status'
              name='status'
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value='ACTIVE'>Active</option>
              <option value='INACTIVE'>Inactive</option>
              <option value='SUSPENDED'>Suspended</option>
            </select>
          </Form.Field>

          <Header as='h3' dividing style={{ marginTop: '30px' }}>
            CRM Integration
            <Header.Subheader>Specify the CRM system used by this brokerage for customer relationship management</Header.Subheader>
          </Header>

          <Form.Field>
            <label htmlFor='brokerage-crm-solution'>CRM Solution Name</label>
            <select
              id='brokerage-crm-solution'
              name='crmSolutionName'
              value={formData.crmSolutionName}
              onChange={handleChange}
            >
              <option value=''>-- Select CRM Solution (Optional) --</option>
              <option value='Salesforce'>Salesforce</option>
              <option value='HubSpot'>HubSpot</option>
              <option value='Microsoft Dynamics 365'>Microsoft Dynamics 365</option>
              <option value='Zoho CRM'>Zoho CRM</option>
              <option value='SugarCRM'>SugarCRM</option>
              <option value='Pipedrive'>Pipedrive</option>
              <option value='Custom CRM'>Custom CRM</option>
              <option value='None'>None</option>
            </select>
          </Form.Field>

          <Header as='h3' dividing style={{ marginTop: '30px' }}>
            Email Configuration
            <Header.Subheader>Configure SMTP settings for sending emails on behalf of this brokerage (e.g., advisor invitations)</Header.Subheader>
          </Header>

          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='brokerage-email-address'>Email Address</label>
              <input
                id='brokerage-email-address'
                type='email'
                name='emailAddress'
                value={formData.emailAddress}
                onChange={handleChange}
                placeholder='noreply@acme-insurance.com'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='brokerage-email-display-name'>Display Name</label>
              <input
                id='brokerage-email-display-name'
                name='emailDisplayName'
                value={formData.emailDisplayName}
                onChange={handleChange}
                placeholder='Acme Insurance'
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='brokerage-smtp-host'>SMTP Host</label>
              <input
                id='brokerage-smtp-host'
                name='smtpHost'
                value={formData.smtpHost}
                onChange={handleChange}
                placeholder='smtp.gmail.com'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='brokerage-smtp-port'>SMTP Port</label>
              <input
                id='brokerage-smtp-port'
                type='number'
                name='smtpPort'
                value={formData.smtpPort}
                onChange={handleChange}
                placeholder='587'
                min='1'
                max='65535'
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths='equal'>
            <Form.Field>
              <label htmlFor='brokerage-smtp-username'>SMTP Username</label>
              <input
                id='brokerage-smtp-username'
                name='smtpUsername'
                value={formData.smtpUsername}
                onChange={handleChange}
                placeholder='noreply@acme-insurance.com'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='brokerage-smtp-password'>SMTP Password</label>
              <input
                id='brokerage-smtp-password'
                type='password'
                name='smtpPassword'
                value={formData.smtpPassword}
                onChange={handleChange}
                placeholder='Enter SMTP password'
              />
            </Form.Field>
          </Form.Group>

          <Form.Group inline>
            <label htmlFor='smtpUseTls'>SMTP Encryption:</label>
            <Form.Field>
              <input
                type='checkbox'
                name='smtpUseTls'
                checked={formData.smtpUseTls}
                onChange={(e) => setFormData(prev => ({ ...prev, smtpUseTls: e.target.checked }))}
                id='smtpUseTls'
              />
              <label htmlFor='smtpUseTls'>Use TLS (recommended for port 587)</label>
            </Form.Field>
            <Form.Field>
              <input
                type='checkbox'
                name='smtpUseSsl'
                checked={formData.smtpUseSsl}
                onChange={(e) => setFormData(prev => ({ ...prev, smtpUseSsl: e.target.checked }))}
                id='smtpUseSsl'
              />
              <label htmlFor='smtpUseSsl'>Use SSL (for port 465)</label>
            </Form.Field>
          </Form.Group>

          <Message info>
            <Message.Header>Common SMTP Providers</Message.Header>
            <Message.List>
              <Message.Item>Gmail: smtp.gmail.com:587 (TLS)</Message.Item>
              <Message.Item>Office 365: smtp.office365.com:587 (TLS)</Message.Item>
              <Message.Item>SendGrid: smtp.sendgrid.net:587 (TLS)</Message.Item>
            </Message.List>
          </Message>

          <EmailTemplateSection
            advisorInvitationTemplate={formData.advisorInvitationTemplate || ''}
            clientInvitationTemplate={formData.clientInvitationTemplate || ''}
            advisorInvitationSubject={formData.advisorInvitationSubject || ''}
            clientInvitationSubject={formData.clientInvitationSubject || ''}
            onChange={handleEmailTemplateChange}
          />

          <Button.Group>
            <Button type='button' onClick={() => navigate('/brokerages')}>
              Cancel
            </Button>
            <Button.Or />
            <Button positive type='submit' disabled={loading}>
              <Icon name='checkmark' />
              {isEditMode ? 'Update Brokerage' : 'Create Brokerage'}
            </Button>
          </Button.Group>
        </Form>
    </Container>
  );
}

export default BrokerageFormPage;
